import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { ToastContent } from '@components/ToastContent';
import { usePbqaEvalUIStore } from '@ContractBuilder/store/pbqa-eval.store';
import { fetchEvalConfigs } from '@queries/evalConfigs';
import type { EvalRunDetail, EvalRunSummary } from '@queries/evalRuns';
import { fetchEvalRun, fetchEvalRuns } from '@queries/evalRuns';
import { AUDIT_LOG_KEY } from '@queries/fetchEntity';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { POLL_INTERVAL_MS } from '../constants';
import type { EvalRunStatus } from '../types';
import { RUN_IS_TERMINAL } from '../types';

import {
  detailPollInterval,
  EVAL_CONFIGS_PICKER_KEY,
  EVAL_RUN_DETAIL_KEY,
  EVAL_RUNS_LIST_KEY,
} from './useEvalRunsData';

/**
 * The guidance counters the backend keeps on the contract's root row
 * (`backend/domain/evals/guidanceCounts.ts`): which completed run they
 * describe, its canonical findings, and how many of those are closed. They
 * arrive with the submission the page has already loaded, so the badge can
 * count from them without a request of its own.
 */
export interface RootGuidanceCounts {
  runId?: string;
  findingsCount?: number;
  reviewedCount?: number;
}

interface UseGuidanceStatusArgs {
  submissionId: string;
  /** Shown in the "guidance ready" toast so it reads right from another tab. */
  contractName?: string;
  enabled: boolean;
  /** The run detail is fetched only while the drawer is open — see `openCount`. */
  isDrawerOpen: boolean;
  rootCounts?: RootGuidanceCounts;
  /**
   * Whether the caller is internal (`pbqa_eval`). A broker's run list arrives
   * from the server already narrowed to the champion config; internal staff
   * get every run, challengers included, so the badge has to narrow it
   * itself — it shows what a broker would see, whatever the admin tools show.
   */
  pbqaEval: boolean;
}

/** What the badge counts: duplicates hidden, closed = has a resolution. */
const countOpenFindings = (detail: EvalRunDetail) =>
  detail.findings.filter((finding) => !finding.superseded_by && !finding.resolution).length;

/**
 * Header-level view of the contract's guidance, mounted for as long as the
 * contract is open — unlike the drawer, which unmounts when closed:
 *
 *   - the open-suggestion count for the header badge, read from the same
 *     react-query keys the drawer uses, so the two never disagree;
 *   - polling the run list while a scan is running, even with the drawer
 *     closed, so the "you can close this panel and keep working" promise holds;
 *   - a toast the moment a scan completes.
 *
 * Only the list is fetched while the drawer is closed. The run detail is the
 * expensive call (the server re-serialises the contract to report
 * `has_unchecked_changes`), so it is enabled only while the drawer is open;
 * with the drawer closed the badge counts from whatever is already at hand.
 */
export function useGuidanceStatus({
  submissionId,
  contractName,
  enabled,
  isDrawerOpen,
  rootCounts,
  pbqaEval,
}: UseGuidanceStatusArgs) {
  const activeRunId = usePbqaEvalUIStore(({ activeRunId }) => activeRunId);
  const queryClient = useQueryClient();

  const runsQuery = useQuery({
    queryKey: EVAL_RUNS_LIST_KEY(submissionId),
    queryFn: () => fetchEvalRuns(submissionId),
    enabled: enabled && Boolean(submissionId),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const runs = (query.state.data ?? []) as EvalRunSummary[];
      return runs.some((run) => !RUN_IS_TERMINAL[run.status as EvalRunStatus]) ? POLL_INTERVAL_MS : false;
    },
  });

  // Same key the drawer's picker uses, so one fetch serves both. Internal
  // only: the route 403s a broker, whose list needs no narrowing anyway.
  const configsQuery = useQuery({
    queryKey: EVAL_CONFIGS_PICKER_KEY,
    queryFn: fetchEvalConfigs,
    enabled: enabled && pbqaEval,
    refetchOnWindowFocus: false,
  });
  const championConfigId = configsQuery.data?.find((config) => config.is_champion)?.id;

  // The badge is the broker view of the contract: champion runs only. A
  // challenger an internal user just fired must not become "N suggestions"
  // in the header (same rule as `visibleRuns` in PbqaEvalController).
  const runs = useMemo(() => {
    const all = runsQuery.data ?? [];
    if (!pbqaEval) return all;
    if (!championConfigId) return [];
    return all.filter((run) => run.eval_config_id === championConfigId);
  }, [championConfigId, pbqaEval, runsQuery.data]);

  // The drawer's selection wins while it is in the list; otherwise the latest
  // run, which is also what the drawer would select on open.
  const latestRunId = activeRunId && runs.some((run) => run.id === activeRunId) ? activeRunId : runs[0]?.id;

  // Disabled while the drawer is closed, but still observing the key: the
  // detail the drawer last fetched stays cached (and counted) after it closes.
  const detailQuery = useQuery({
    queryKey: latestRunId ? EVAL_RUN_DETAIL_KEY(submissionId, latestRunId) : ['pbqa-eval', 'run', 'missing'],
    queryFn: () => fetchEvalRun(submissionId, latestRunId!),
    enabled: enabled && isDrawerOpen && Boolean(submissionId) && Boolean(latestRunId),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => detailPollInterval(query.state.data as EvalRunDetail | undefined),
  });

  // Announce a completion once: the transition of a run we have already seen
  // in a non-terminal state into SUCCEEDED. Runs that are already complete on
  // first load are not news.
  const seenStatuses = useRef(new Map<string, EvalRunStatus>());
  useEffect(() => {
    for (const run of runs) {
      const previous = seenStatuses.current.get(run.id);
      if (previous && !RUN_IS_TERMINAL[previous] && run.status === 'SUCCEEDED') {
        // The scan's completion is an Activity entry, but the completing write never touches the
        // contract's `updated_at` the audit query is keyed on — so refetch it explicitly.
        queryClient.invalidateQueries({ queryKey: AUDIT_LOG_KEY(submissionId) });
        // Whatever detail the drawer cached for this run is an in-flight snapshot
        // now; drop it so the next open fetches the consolidated result.
        queryClient.invalidateQueries({ queryKey: EVAL_RUN_DETAIL_KEY(submissionId, run.id) });
        const count = run.findings_count ?? 0;
        toast.success(
          <ToastContent
            title="Contract Guidance Ready"
            description={`You have ${count} suggestion${count === 1 ? '' : 's'} to review${contractName ? ` on ${contractName}` : ''}`}
          />,
          { autoClose: 8_000 },
        );
      }
      seenStatuses.current.set(run.id, run.status as EvalRunStatus);
    }
  }, [contractName, queryClient, runs, submissionId]);

  /*
   * Badge count, cheapest source that is right first:
   *   1. the run detail whenever the cache holds one OF THE FINISHED RUN —
   *      live while the drawer is open, kept after it closes. A detail
   *      fetched mid-scan (the drawer was open, then closed before the run
   *      finished) still shows the run as running and carries pre-dedup
   *      rows, so it is skipped in favour of the sources below;
   *   2. the root-row counters, when they describe the run the badge follows
   *      (the common case on page load: last scan done, nothing since);
   *   3. the list's `findings_count`, for a scan that has just completed
   *      with the drawer closed — nothing is reviewed yet, so all are open.
   */
  const detail = detailQuery.data;
  const { runId: rootRunId, findingsCount: rootFindings, reviewedCount: rootReviewed } = rootCounts ?? {};
  // Only a completed scan has a count worth showing: while one runs, finding
  // rows land lens by lens and are not yet de-duplicated, so a live tally
  // would climb into the hundreds and then collapse.
  const latestRun = runs.find((run) => run.id === latestRunId);
  const latestIsComplete = latestRun?.status === 'SUCCEEDED';
  const openCount = useMemo(() => {
    if (!latestRunId || !latestIsComplete) return 0;
    if (detail && detail.run.id === latestRunId && RUN_IS_TERMINAL[detail.run.status as EvalRunStatus]) {
      return countOpenFindings(detail);
    }
    if (rootRunId === latestRunId && rootFindings !== undefined) {
      return Math.max(0, rootFindings - (rootReviewed ?? 0));
    }
    return latestRun?.findings_count ?? 0;
  }, [detail, latestRunId, latestIsComplete, latestRun, rootRunId, rootFindings, rootReviewed]);

  const isScanning = runs.some((run) => !RUN_IS_TERMINAL[run.status as EvalRunStatus]);
  // A completed scan with nothing left to review — the badge shows a tick so
  // "has it been run?" is answerable at a glance.
  const isComplete = latestIsComplete && !isScanning && openCount === 0;

  return { openCount, isScanning, isComplete };
}
